import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { BarChart3, Building2, FileText, Scale, ClipboardCheck, Database, Microscope, FlaskConical, Pill, Beaker, CheckCircle, Users, Star, ThumbsUp, GraduationCap, RotateCcw, Phone, BadgeCheck, ChevronDown, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SplitBanner from "@/components/ui/SplitBanner";
import { Link } from "@/lib/router-compat";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import { ivdCroT } from "@/i18n/ivdCro";
import useEmblaCarousel from "embla-carousel-react";

const useCountUp = (target: number, duration = 1.2, startOnView = true) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!startOnView || !ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();
          const step = (now: number) => {
            const progress = Math.min((now - start) / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, startOnView]);

  return { count, ref };
};

const AnimatedStat = ({ value, className }: { value: string; className?: string }) => {
  const numMatch = value.match(/^([\d.]+)/);
  if (!numMatch) return <span className={className}>{value}</span>;

  const num = parseFloat(numMatch[1]);
  const suffix = value.slice(numMatch[1].length);
  const isDecimal = numMatch[1].includes(".");
  const { count, ref } = useCountUp(isDecimal ? num * 10 : num, 1.4);
  const display = isDecimal ? (count / 10).toFixed(1) : count.toString();

  return <span ref={ref} className={className}>{display}{suffix}</span>;
};
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Case images
import ivdrWhoStrategyImg from "@/assets/services/clinical-solutions/cases/ivdr-who-strategy.jpg";
import ivdrMarketAccessImg from "@/assets/services/clinical-solutions/cases/ivdr-market-access.png";
import multiplexStiImg from "@/assets/services/clinical-solutions/cases/multiplex-sti-pcr.png";
import studySponsorshipImg from "@/assets/services/clinical-solutions/cases/study-sponsorship.jpg";
import ivdrWhoComboImg from "@/assets/services/clinical-solutions/cases/ivdr-who-combo.jpg";
import qbdIvdQaradLogo from "@/assets/services/clinical-solutions/qbd-ivd-qarad.png";
import ivdImageBreak from "@/assets/services/clinical-solutions/ivd-image-break.png";

const authorSlug = (name: string) => name.toLowerCase().replace(/\s+/g, "-");

const COLOR = "#FFC60C";

const anim = (i: number) => ({
  initial: { opacity: 0, y: 16 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true } as const,
  transition: { duration: 0.4, delay: i * 0.08 },
});

import AnchorNav from "./AnchorNav";

/* ── Expert card ── */
const ExpertCard = ({
  person,
  index,
  onCtaClick,
}: {
  person: { name: string; role: string; experience?: string; bullets: string[]; image?: string };
  index: number;
  onCtaClick?: () => void;
}) => {
  const lang = useLanguage();
  const pre = langPrefix(lang);
  const initials = person.name.split(" ").map(n => n[0]).join("");
  return (
    <motion.div
      className="flex gap-4 items-start bg-muted/30 rounded-xl p-5 border border-border/40 h-full"
      {...anim(index)}
    >
      <Link to={`${pre}/author/${authorSlug(person.name)}`} className="shrink-0">
        {person.image ? (
          <img
            src={person.image}
            alt={person.name}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover hover:ring-2 hover:ring-[var(--service-color)] transition-all"
            loading="lazy"
          />
        ) : (
          <div
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-xl font-bold hover:ring-2 transition-all"
            style={{ backgroundColor: `${COLOR}20`, color: COLOR, borderColor: COLOR }}
          >
            {initials}
          </div>
        )}
      </Link>
      <div className="min-w-0">
        <Link to={`${pre}/author/${authorSlug(person.name)}`} className="hover:underline">
          <h3 className="text-base font-semibold text-foreground">{person.name}</h3>
        </Link>
        <p className="text-sm text-muted-foreground mb-1">{person.role}</p>
        {person.experience && (
          <p className="text-sm font-medium text-foreground mb-2">{person.experience}</p>
        )}
        <ul className="text-sm text-muted-foreground space-y-1">
          {person.bullets.map((b, j) => (
            <li key={j} className="flex gap-2.5">
              <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: COLOR }} />
              {b}
            </li>
          ))}
        </ul>
        {onCtaClick && (
          <button onClick={onCtaClick} className="mt-3 text-sm font-medium text-primary hover:underline">
            {uiT(lang)("getInTouchArrow")}
          </button>
        )}
      </div>
    </motion.div>
  );
};

/* ── Experts carousel ── */
const ExpertsCarousel = ({ onCtaClick, experts, sectionTitle, sectionSubtitle }: { onCtaClick: () => void; experts: { name: string; role: string; experience?: string; bullets: string[]; image?: string }[]; sectionTitle: string; sectionSubtitle: string }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, slidesToScroll: 1 });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const lang = useLanguage();
  const i = ivdCroT(lang);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => { emblaApi.off("select", onSelect); emblaApi.off("reInit", onSelect); };
  }, [emblaApi, onSelect]);

  return (
    <section id="ivd-experts" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i("expertsOverline")}</p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{sectionTitle}</h2>
        <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">
          {sectionSubtitle}
        </p>
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-5 items-stretch">
              {experts.map((person, idx) => (
                <div key={person.name} className="flex-[0_0_78%] sm:flex-[0_0_44%] min-w-0 flex">
                  <div className="flex-1">
                    <ExpertCard person={person} index={idx} onCtaClick={onCtaClick} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {(canPrev || canNext) && (
            <div className="flex items-center gap-2 mt-5 justify-end">
              <button
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canPrev}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canNext}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

interface Props {
  onCtaClick: () => void;
  offeringsSlot?: React.ReactNode;
}

const IVDPerformanceContent = ({ onCtaClick, offeringsSlot }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const i = ivdCroT(lang);

  const anchorSections = [
    { id: "ivd-intro", label: i("anchorOverview") },
    { id: "ivd-services", label: i("anchorServices") },
    { id: "ivd-expertise", label: i("anchorExpertise") },
    { id: "ivd-experts", label: i("anchorExperts") },
    { id: "ivd-stats", label: i("anchorStats") },
    { id: "ivd-cases", label: i("anchorCases") },
  ];

  const services = [
    { icon: BarChart3, title: i("svcStudyStrategy"), description: i("svcStudyStrategyDesc") },
    { icon: Building2, title: i("svcSiteSelection"), description: i("svcSiteSelectionDesc") },
    { icon: FileText, title: i("svcProtocol"), description: i("svcProtocolDesc") },
    { icon: Scale, title: i("svcRegulatory"), description: i("svcRegulatoryDesc") },
    { icon: ClipboardCheck, title: i("svcTraining"), description: i("svcTrainingDesc") },
    { icon: Database, title: i("svcDataAnalysis"), description: i("svcDataAnalysisDesc") },
  ];

  const expertiseAreas = [
    {
      icon: Microscope,
      title: i("expertInfectious"),
      items: ["Serology (HIV, HBV, HCV, Syphilis)", "Respiratory diseases (Covid-19, Influenza A/B, RSV)", "STIs", "Tropical diseases (Malaria, Dengue)", "Gastrointestinal (C. difficile, H. Pylori)"],
    },
    {
      icon: FlaskConical,
      title: i("expertOncology"),
      items: ["Bladder cancer", "Breast cancer", "Lymphoma", "Liver cancer (HCC)", "Colorectal cancer (mCRC)", "Pancreatic cancer", "Non-Small Cell Lung Cancer (NSCLC)"],
    },
    {
      icon: Pill,
      title: i("expertEndocrinology"),
      items: ["Fertility (hCG, LH)", "Diabetes (Glucose, HbA1C)"],
    },
    {
      icon: Beaker,
      title: i("expertOther"),
      items: ["Blood grouping", "Hemoglobin", "Troponin I", "Lipoprotein (a)"],
    },
  ];

  const whyReasons = [
    { title: i("whyRegulatory"), description: i("whyRegulatoryDesc") },
    { title: i("whyNetwork"), description: i("whyNetworkDesc") },
    { title: i("whyTailored"), description: i("whyTailoredDesc") },
    { title: i("whyDiverse"), description: i("whyDiverseDesc") },
    { title: i("whyTrackRecord"), description: i("whyTrackRecordDesc") },
    { title: i("whyNotifiedBodies"), description: i("whyNotifiedBodiesDesc") },
    { title: i("whyCertified"), description: i("whyCertifiedDesc") },
  ];

  const stats = [
    { icon: FlaskConical, value: "288", label: i("statClinicalStudies"), sub: i("statClinicalStudiesSub") },
    { icon: Users, value: "100+", label: i("statHappyClients"), sub: i("statHappyClientsSub") },
    { icon: Star, value: "4.5/5", label: i("statAvgRating"), sub: i("statAvgRatingSub") },
    { icon: ThumbsUp, value: "4.5/5", label: i("statRecommended"), sub: i("statRecommendedSub") },
    { icon: GraduationCap, value: "4.8/5", label: i("statExpertise"), sub: i("statExpertiseSub") },
    { icon: RotateCcw, value: "4.6/5", label: i("statReuse"), sub: i("statReuseSub") },
    { icon: Phone, value: "4.6/5", label: i("statComms"), sub: i("statCommsSub") },
    { icon: BadgeCheck, value: "4.6/5", label: i("statQuality"), sub: i("statQualitySub") },
  ];

  const clientCases = [
    {
      title: i("case1Title"),
      subtitle: i("case1Subtitle"),
      tag: "Class D",
      bullets: [i("case1B1"), i("case1B2"), i("case1B3")],
      image: ivdrWhoStrategyImg,
    },
    {
      title: i("case2Title"),
      subtitle: i("case2Subtitle"),
      tag: "IVDR",
      bullets: [i("case2B1"), i("case2B2"), i("case2B3")],
      image: ivdrMarketAccessImg,
    },
    {
      title: i("case3Title"),
      subtitle: i("case3Subtitle"),
      tag: "PCR",
      bullets: [i("case3B1"), i("case3B2"), i("case3B3")],
      image: multiplexStiImg,
    },
    {
      title: i("case4Title"),
      subtitle: i("case4Subtitle"),
      tag: "Sponsorship",
      bullets: [i("case4B1"), i("case4B2"), i("case4B3")],
      image: studySponsorshipImg,
    },
  ];

  const ivdExperts = [
    {
      name: "Annelies Rotthier",
      role: i("expertRole1"),
      experience: i("expertExp1"),
      bullets: [
        "IVD Clinical Evidence and Medical Writing",
        "IVD Product Development and IVDR Compliance",
        "Strategic leadership & Consultancy",
      ],
      image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/webinar-images/authors/annelies-rotthier.png",
    },
    {
      name: "Annelies Rombout",
      role: i("expertRole2"),
      experience: i("expertExp2"),
      bullets: [
        "International experience in R&D and clinical development",
        "Expert in analytical and clinical performance study design, execution, and reporting",
        "Specialized in nucleic acid and protein-based IVD assays, including HLA-typing, infectious diseases, Cystic Fibrosis, Vitamin D, and cardiac diagnostics",
      ],
      image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/webinar-images/authors/annelies-rombout.png",
    },
    {
      name: "Sara Peeters",
      role: i("expertRole3"),
      experience: i("expertExp3"),
      bullets: [
        "International experience in assay development, surface chemistry, and molecular diagnostics",
        "Expert in biosensors, assay optimization, and performance evaluation studies",
        "Manages analytical and clinical performance studies for a wide range of IVD products",
      ],
      image: "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/webinar-images/authors/sara-peeters.png",
    },
  ];

  return (
    <>
      {/* Anchor nav */}
      <AnchorNav sections={anchorSections} />

      {/* Intro */}
      <section id="ivd-intro" className="py-14 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl space-y-4">
          <p className="text-base text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: i("introP1") }} />
          <p className="text-base text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: i("introP2") }} />
          <p className="text-base text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: i("introP3") }} />
          <p className="text-base text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: i("introP4") }} />
        </div>
      </section>

      {/* How can we help - Services */}
      <section id="ivd-services" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i("servicesOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{i("servicesTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-4 max-w-3xl">{i("servicesP1")}</p>
          <p className="text-base text-muted-foreground leading-relaxed mb-4 max-w-3xl">{i("servicesP2")}</p>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">{i("servicesP3")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <motion.div key={svc.title} className="flex gap-4 items-start bg-background rounded-xl p-5 shadow-sm border border-border/40" {...anim(idx)}>
                  <Icon size={28} className="mt-0.5 shrink-0" style={{ color: COLOR }} strokeWidth={1.5} />
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-1.5">{svc.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{svc.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image break */}
      <SplitBanner
        title={i("splitTitle")}
        description={i("splitDesc")}
        image={ivdImageBreak}
        imageAlt="IVD laboratory specialist reviewing diagnostic data"
        ctaLabel={i("splitCta")}
        onCtaClick={onCtaClick}
        accentColor={COLOR}
        reversed
      />

      {/* Expertise */}
      <section id="ivd-expertise" className="py-16 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i("expertiseOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{i("expertiseTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-4 max-w-3xl">{i("expertiseP1")}</p>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">{i("expertiseP2")}</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {expertiseAreas.map((area, idx) => {
              const Icon = area.icon;
              return (
                <motion.div key={area.title} className="bg-muted/30 rounded-xl p-6 border border-border/40" {...anim(idx)}>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon size={28} style={{ color: COLOR }} strokeWidth={1.5} />
                    <h3 className="text-lg font-semibold text-foreground">{area.title}</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    {area.items.map((item, j) => (
                      <li key={j} className="flex gap-2.5">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: COLOR }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Meet our experts – carousel */}
      <ExpertsCarousel onCtaClick={onCtaClick} experts={ivdExperts} sectionTitle={i("expertsTitle")} sectionSubtitle={i("expertsSubtitle")} />

      {/* Client Satisfaction Stats */}
      <section id="ivd-stats" className="py-16 sm:py-20 bg-muted/30 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i("statsOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{i("statsTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">{i("statsSubtitle")}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <motion.div key={stat.label} className="bg-background rounded-xl p-5 border border-border/40 text-center" {...anim(idx)}>
                  <Icon size={28} className="mx-auto mb-3" style={{ color: COLOR }} strokeWidth={1.5} />
                  <AnimatedStat value={stat.value} className="text-2xl font-bold text-foreground block" />
                  <p className="text-sm font-medium text-foreground mb-1.5">{stat.label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{stat.sub}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Offerings slot */}
      {offeringsSlot}

      {/* Client Cases */}
      <section id="ivd-cases" className="py-16 sm:py-20 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-0.5 rounded-full" style={{ backgroundColor: COLOR }} />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{i("casesOverline")}</p>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">{i("casesTitle")}</h2>
          <p className="text-base text-muted-foreground leading-relaxed mb-10 max-w-3xl">{i("casesSubtitle")}</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {clientCases.map((c, idx) => (
              <motion.div
                key={c.title}
                className="group rounded-xl overflow-hidden border border-border/40 bg-muted/30 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                {...anim(idx)}
              >
                <div className="relative">
                  <img src={c.image} alt={c.title} className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                  <span
                    className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide"
                    style={{ backgroundColor: COLOR, color: "#1a1a1a" }}
                  >
                    {c.tag}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-1.5">{c.title}</h3>
                  <p className="text-sm italic text-muted-foreground mb-3">{c.subtitle}</p>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    {c.bullets.map((b, j) => {
                      const colonIdx = b.indexOf(":");
                      const label = colonIdx > -1 ? b.slice(0, colonIdx) : null;
                      const rest = colonIdx > -1 ? b.slice(colonIdx + 1) : b;
                      return (
                        <li key={j} className="flex gap-2.5">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: COLOR }} />
                          <span>
                            {label && <span className="font-semibold text-foreground">{label}:</span>}
                            {rest}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <button onClick={onCtaClick} className="mt-4 text-sm font-medium text-primary hover:underline">
                    {t("getInTouchArrow")}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </>
  );
};

export const ivdFaqItems = (lang: import("@/hooks/useLanguage").SiteLang) => {
  const i = ivdCroT(lang);
  return [
    { question: i("faq1Q"), answer: i("faq1A") },
    { question: i("faq2Q"), answer: i("faq2A") },
  ];
};

export default IVDPerformanceContent;
